import api from '../api';

/**
 * Payment Service
 * Handles all payment-related API calls
 */

const paymentService = {
  /**
   * Create a payment order
   * @param {Object} data - Order data
   * @param {string} data.type - 'credit' or 'bundle'
   * @param {string} data.communicationType - 'email' or 'sms' (for credit purchases)
   * @param {number} data.credits - Number of credits (for credit purchases)
   * @param {string} data.bundleId - Bundle ID (for bundle purchases)
   * @returns {Promise} Order details
   */
  createOrder: async (data) => {
    try {
      const response = await api.post('/api/payment/create-order', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Verify payment after Razorpay redirect
   * @param {Object} data - Verification data
   * @param {string} data.razorpayOrderId - Razorpay order ID
   * @param {string} data.razorpayPaymentId - Razorpay payment ID
   * @param {string} data.razorpaySignature - Razorpay signature
   * @param {string} data.paymentId - Our payment record ID
   * @returns {Promise} Verification result
   */
  verifyPayment: async (data) => {
    try {
      const response = await api.post('/api/payment/verify', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get payment status
   * @param {string} paymentId - Payment record ID
   * @returns {Promise} Payment status
   */
  getPaymentStatus: async (paymentId) => {
    try {
      const response = await api.get(`/api/payment/status/${paymentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get order details
   * @param {string} orderId - Payment record ID
   * @returns {Promise} Order details
   */
  getOrder: async (orderId) => {
    try {
      const response = await api.get(`/api/payment/order/${orderId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Initiate refund (Super Admin only)
   * @param {Object} data - Refund data
   * @param {string} data.paymentId - Payment record ID
   * @param {number} data.amount - Refund amount
   * @param {string} data.notes - Refund notes
   * @returns {Promise} Refund result
   */
  initiateRefund: async (data) => {
    try {
      const response = await api.post('/api/payment/refund', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get refund status
   * @param {string} paymentId - Payment record ID
   * @returns {Promise} Refund status
   */
  getRefundStatus: async (paymentId) => {
    try {
      const response = await api.get(`/api/payment/refund/${paymentId}/status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get all refunds (Super Admin only)
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} List of refunds
   */
  getAllRefunds: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/api/payment/refunds?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default paymentService;